import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Robot,
  CircleNotch,
  ListChecks,
  TextT,
  ListBullets,
  CursorClick,
  CheckSquare,
} from "@phosphor-icons/react";

const TYPE_META = {
  list: { icon: ListBullets, label: "List", tint: "#A78BFA" },
  buttons: { icon: CursorClick, label: "Buttons", tint: "#60A5FA" },
  confirm: { icon: CheckSquare, label: "Confirm", tint: "#10B981" },
  text: { icon: TextT, label: "Text", tint: "#94A3B8" },
};

function StepCard({ step, idx }) {
  const meta = TYPE_META[step.interactive] || TYPE_META.text;
  const Icon = meta.icon;
  return (
    <div
      className="rounded-lg border border-[var(--border)] bg-[var(--card)] overflow-hidden"
      data-testid={`flow-step-${idx}`}
    >
      <div className="px-4 py-3 border-b border-[var(--border)] flex items-center gap-2.5">
        <div
          className="h-8 w-8 rounded-md grid place-items-center shrink-0"
          style={{ background: `${meta.tint}1f`, color: meta.tint }}
        >
          <Icon size={16} weight="duotone" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold heading leading-tight truncate">
            {step.title}
          </div>
          {step.trigger && (
            <div className="text-[11px] text-[var(--muted)] truncate">
              {step.trigger}
            </div>
          )}
        </div>
        <span
          className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full font-semibold"
          style={{ background: `${meta.tint}1f`, color: meta.tint }}
        >
          {meta.label}
        </span>
      </div>

      <div className="p-4 space-y-3">
        <pre
          className="whitespace-pre-wrap text-[13px] leading-relaxed font-sans bg-[var(--bg)] rounded-md p-3 border border-[var(--border)]"
          data-testid={`flow-step-text-${idx}`}
        >
          {step.text}
        </pre>

        {step.options && step.options.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-wide text-[var(--muted)] font-semibold mb-1.5">
              Options
            </div>
            <ul className="space-y-1">
              {step.options.map((opt, i) => (
                <li
                  key={i}
                  className="text-[12px] px-2.5 py-1.5 rounded-md bg-[var(--bg)] border border-[var(--border)] text-[var(--fg)]"
                >
                  {opt}
                </li>
              ))}
            </ul>
          </div>
        )}

        {step.notes && (
          <div className="text-[11px] text-[var(--muted)] italic border-l-2 border-[var(--border)] pl-2.5">
            {step.notes}
          </div>
        )}
      </div>
    </div>
  );
}

export default function RodbezFlow() {
  const navigate = useNavigate();
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api
      .get("/rodbez/flow")
      .then((r) => setSteps(r.data?.steps || []))
      .catch((e) =>
        setErr(e?.response?.data?.detail || e.message || "Load failed"),
      )
      .finally(() => setLoading(false));
  }, []);

  return (
    <div
      className="min-h-screen bg-[var(--bg)] text-[var(--fg)]"
      data-testid="rodbez-flow-page"
    >
      <header className="sticky top-0 z-10 border-b border-[var(--border)] bg-[var(--card)]/90 backdrop-blur supports-[backdrop-filter]:bg-[var(--card)]/70">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/")}
            data-testid="flow-back-btn"
            aria-label="Back to Inbox"
          >
            <ArrowLeft size={18} />
          </Button>
          <div className="flex items-center gap-2 min-w-0">
            <Robot
              size={22}
              weight="fill"
              className="text-[#10B981] shrink-0"
            />
            <div className="min-w-0">
              <div className="text-base sm:text-lg font-semibold heading leading-tight truncate">
                RodBez Booking Bot — Flow Preview
              </div>
              <div className="text-[11px] text-[var(--muted)] leading-tight">
                Read-only · {steps.length} steps · {`{placeholders}`} are filled
                at runtime
              </div>
            </div>
          </div>
          <ListChecks
            size={22}
            weight="duotone"
            className="ml-auto text-[var(--muted)] hidden sm:block"
          />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        {loading ? (
          <div className="py-12 flex items-center justify-center text-[var(--muted)] text-sm gap-2">
            <CircleNotch size={18} className="animate-spin" /> Loading flow…
          </div>
        ) : err ? (
          <div className="py-12 text-center text-sm text-[#EF4444]">{err}</div>
        ) : (
          <div className="space-y-3">
            {steps.map((s, i) => (
              <StepCard key={i} step={s} idx={i} />
            ))}
          </div>
        )}

        <div className="mt-6 rounded-lg border border-[var(--border)] bg-[var(--card)] p-4 text-[12px] text-[var(--muted)] space-y-1">
          <div className="font-semibold text-[var(--fg)] heading">Notes</div>
          <div>
            • Placeholders like <code className="font-mono">{`{name}`}</code>{" "}
            are replaced at runtime with real values from the customer's
            session and the RodBez API.
          </div>
          <div>
            • To change any wording, edit{" "}
            <code className="font-mono">/app/backend/rodbez_booking.py</code>{" "}
            and re-deploy.
          </div>
          <div>
            • To temporarily disable the bot, use the{" "}
            <strong>Bot ON/OFF</strong> button in the top bar → toggle{" "}
            <strong>RodBez Booking</strong> off.
          </div>
        </div>
      </main>
    </div>
  );
}
